// @flow

import React, { Component } from 'react';
import Helmet from 'react-helmet';
import onClickOutside from 'react-onclickoutside';
import breakpoint from 'styled-components-breakpoint';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { format } from 'date-fns';

import theme from '../../../../theme';
import Button, { LinkButton } from '../../../../components/Button';

import PeriodPresets from '../../components/Period';
import DatePeriod from '../../components/DatePeriod';

const BORDER_COLOR = theme.palette.borders[5];
const BG_LIGHT_COLOR = theme.palette.backgrounds[2];
const BG_COLOR = theme.palette.success[1];

type Props = {
  selectorFrom: Date | null,
  selectorTo: Date | null,
  from: Date | null,
  to: Date | null,
  children: any,
  isMonthsPresets?: boolean,
  datePresets?: Array<{
    periodDays: number,
    fromDate: Date,
    toDate: Date,
  }>,
  subscriptionIsActive: boolean,
  premiumPlan?: boolean,
  onResetClick: () => any,
  onApplyPeriod: (any, any) => any,
  onChangePreset: (fromDate: Date, toDate: Date) => any,
};

type State = {
  isOpen: boolean,
};

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-flow: column;

  ${breakpoint('tablet')`
    flex-flow: row;
  `};
`;

const DropDownWrapper = styled.div`
  position: absolute;
  width: auto;
  z-index: 10;
  top: 100%;
  left: 0;
  padding: 10px;
  padding-bottom: 25px;
  background-color: white;
  box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.06);
  border: solid 1px ${BORDER_COLOR};

  ${breakpoint('tablet')`
    width: 555px;
  `};
`;

const DropDownNav = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: 21px;
`;

const DayPickerWrapper = styled.div`
  display: flex;
  margin-bottom: 10px;
`;

const DatePeriodWrapper = styled.div`
  order: 1;

  ${breakpoint('tablet')`
    order: -1;
  `};
`;

const PeriodPresetsWrapper = styled.div`
  margin-bottom: 10px;

  ${breakpoint('tablet')`
    margin-bottom: 0;
  `};
`;

class DayPickerComponent extends Component<Props, State> {
  state = {
    isOpen: false,
  };

  handleClickOutside = e => {
    if (!this.state.isOpen) {
      return;
    }

    this.setState({ isOpen: false });
  };

  handleOnDatePeriodClick = () => {
    if (!this.state.isOpen) {
      this.setState({ isOpen: true });
      return;
    }
    this.setState({ isOpen: false });
  };

  handleOnChangePreset = (fromDate: Date, toDate: Date) => {
    if (this.state.isOpen) {
      this.setState({ isOpen: false });
    }
    this.props.onChangePreset(fromDate, toDate);
  };

  handleApplyPeriod = (formattedISOFrom: string, formattedISOTo: string) => {
    if (this.state.isOpen) {
      this.setState({ isOpen: false });
    }
    this.props.onApplyPeriod(formattedISOFrom, formattedISOTo);
  };

  render() {
    const formattedISOSelectorFrom = format(
      this.props.selectorFrom,
      'YYYY-MM-DD',
    );
    const formattedISOSelectorTo = format(this.props.selectorTo, 'YYYY-MM-DD');
    const formattedISOFrom = format(this.props.from, 'YYYY-MM-DD');
    const formattedISOTo = format(this.props.to, 'YYYY-MM-DD');

    return (
      <Wrapper>
        <DatePeriodWrapper>
          <DatePeriod
            fromDate={formattedISOSelectorFrom}
            toDate={formattedISOSelectorTo}
            onPeriodClick={this.handleOnDatePeriodClick}
            subscriptionIsActive={this.props.subscriptionIsActive}
            premiumPlan={this.props.premiumPlan}
          />
        </DatePeriodWrapper>
        {this.props.datePresets && (
          <PeriodPresetsWrapper>
            <PeriodPresets
              selectedFrom={this.props.from}
              selectedTo={this.props.to}
              onChange={this.handleOnChangePreset}
              isMonthsPresets={this.props.isMonthsPresets}
              datePresets={this.props.datePresets}
              subscriptionIsActive={this.props.subscriptionIsActive}
              premiumPlan={this.props.premiumPlan}
            />
          </PeriodPresetsWrapper>
        )}
        {this.state.isOpen && (
          <DropDownWrapper>
            <DayPickerWrapper>{this.props.children}</DayPickerWrapper>
            <DropDownNav>
              <LinkButton
                onClick={this.props.onResetClick}
                style={{ marginRight: '20px' }}
                fontSize="16px"
              >
                <FormattedMessage id="common.clear" defaultMessage="Очистить" />
              </LinkButton>
              <Button
                disabled={!this.props.from || !this.props.to}
                backgroundColor={BG_COLOR}
                height="36px"
                fontSize="16px"
                lineHeight="22px"
                onClick={() => {
                  this.handleApplyPeriod(formattedISOFrom, formattedISOTo);
                }}
              >
                <FormattedMessage
                  id="common.accept"
                  defaultMessage="Применить"
                />
              </Button>
            </DropDownNav>
          </DropDownWrapper>
        )}
        <Helmet>
          <style>
            {`
              .RangeDayPicker .DayPicker-Day--selected:not(.DayPicker-Day--start):not(.DayPicker-Day--end):not(.DayPicker-Day--outside) {
                background-color: ${BG_LIGHT_COLOR} !important;
                color: ${BG_COLOR};
              }
              .RangeDayPicker .DayPicker-Day--selected:not(.DayPicker-Day--start):not(.DayPicker-Day--end):not(.DayPicker-Day--outside):hover {
                opacity: 0.7;
              }
              .RangeDayPicker .DayPicker-Day {
                border-radius: 0 !important;
              }
              .RangeDayPicker .DayPicker-Day--start:not(.DayPicker-Day--outside),  .RangeDayPicker .DayPicker-Day--end:not(.DayPicker-Day--outside) {
                background-color: ${BG_COLOR} !important;
              }
              .RangeDayPicker .DayPicker-Day--start:not(.DayPicker-Day--outside):hover,  .RangeDayPicker .DayPicker-Day--end:not(.DayPicker-Day--outside):hover {
                opacity: 0.7;
              }
              .RangeDayPicker .DayPicker-Day:focus {
                outline: none;
              }
              .RangeDayPicker .DayPicker-Caption > div {
                text-align: center;
              }
              .RangeDayPicker .DayPicker-NavBar .DayPicker-NavButton {
                top: 13px;
              }
              .RangeDayPicker .DayPicker-NavBar .DayPicker-NavButton--prev {
                left: 1.5rem;
                right: auto;
              }
            `}
          </style>
        </Helmet>
      </Wrapper>
    );
  }
}

export default onClickOutside(DayPickerComponent);



// WEBPACK FOOTER //
// ./src/buisness/CommonFilters/filters/DayPicker/index.js